import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Ban, CalendarDays, Check, ChevronLeft, ChevronRight, List, MessageCircle, Pencil, Phone, Plus, Trash2, Users, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_LABELS = {
  en_attente: "À valider",
  en_attente_paiement: "Paiement en cours",
  confirmee: "Confirmée",
  refusee: "Refusée",
  annulee: "Annulée",
};

const emptyReservation = {
  name: "",
  email: "",
  phone: "",
  date: "",
  time: "",
  guests: 2,
  message: "",
  status: "confirmee",
  table_name: "",
};

const waReminderLink = (r) => {
  if (!r.phone) return null;
  const num = r.phone.replace(/[^0-9]/g, "").replace(/^0/, "33");
  const text = encodeURIComponent(
    `Bonjour ${r.name}, nous vous confirmons votre réservation au Palais Maasaï le ${r.date} à ${r.time} pour ${r.guests} personne(s). À très vite !`
  );
  return `https://wa.me/${num}?text=${text}`;
};

const MONTH_NAMES = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function ReservationsManager() {
  const [sub, setSub] = useState("liste");
  const [items, setItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newTable, setNewTable] = useState({ name: "", seats: 2 });
  const [newSlot, setNewSlot] = useState({ date: "", time: "", reason: "" });
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [calDay, setCalDay] = useState(null);

  const load = () => {
    api.get("/admin/reservations").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/tables").then((r) => setTables(r.data)).catch(() => {});
    api.get("/admin/blocked-slots").then((r) => setBlocked(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/reservations/${id}`, { status });
    toast.success("Statut mis à jour.");
    if (status === "confirmee") {
      const r = items.find((x) => x.id === id);
      const wa = r && waReminderLink(r);
      if (wa) {
        window.open(wa, "_blank");
        toast.info("WhatsApp s'ouvre : envoyez la confirmation au client en un clic.");
      }
    }
    load();
  };
  const remove = async (id) => {
    await api.delete(`/admin/reservations/${id}`);
    toast.success("Réservation supprimée.");
    load();
  };
  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/reminders/run");
      toast.success(`${data.reminders_sent} rappel(s) envoyé(s) pour demain.`);
      load();
    } catch {
      toast.error("Erreur lors de l'envoi des rappels.");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, guests: parseInt(editing.guests, 10) || 1 };
      delete payload.id;
      delete payload.created_at;
      delete payload.deposit_amount;
      delete payload.estimated_total;
      delete payload.pre_order;
      delete payload.source;
      delete payload.reminder_sent;
      if (editing.id) {
        await api.put(`/admin/reservations/${editing.id}`, payload);
      } else {
        await api.post("/admin/reservations", payload);
      }
      toast.success("Réservation enregistrée.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  const addTable = async (e) => {
    e.preventDefault();
    if (!newTable.name) return;
    await api.post("/admin/tables", { ...newTable, seats: parseInt(newTable.seats, 10) || 2 });
    toast.success("Table ajoutée.");
    setNewTable({ name: "", seats: 2 });
    load();
  };
  const removeTable = async (id) => {
    await api.delete(`/admin/tables/${id}`);
    toast.success("Table supprimée.");
    load();
  };

  const addSlot = async (e) => {
    e.preventDefault();
    if (!newSlot.date) {
      toast.error("Choisissez une date.");
      return;
    }
    await api.post("/admin/blocked-slots", newSlot);
    toast.success("Créneau bloqué.");
    setNewSlot({ date: "", time: "", reason: "" });
    load();
  };
  const removeSlot = async (id) => {
    await api.delete(`/admin/blocked-slots/${id}`);
    toast.success("Créneau débloqué.");
    load();
  };

  const byDay = useMemo(() => {
    const map = {};
    for (const r of items) {
      if (r.status === "annulee" || r.status === "refusee") continue;
      map[r.date] = map[r.date] || [];
      map[r.date].push(r);
    }
    return map;
  }, [items]);

  const calendarCells = useMemo(() => {
    const { year, month } = calMonth;
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, iso, reservations: byDay[iso] || [], blocked: blocked.some((b) => b.date === iso) });
    }
    return cells;
  }, [calMonth, byDay, blocked]);

  const shiftMonth = (delta) => {
    setCalMonth((m) => {
      const d = new Date(m.year, m.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
    setCalDay(null);
  };

  const renderReservation = (r) => (
    <div key={r.id} className={`flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg border p-5 ${r.status === "en_attente" ? "border-copper/60 bg-copper/10" : "border-white/10 bg-coal/60"}`} data-testid={`reservation-${r.id}`}>
      {r.status === "en_attente" && (
        <div className="flex w-full flex-wrap items-center gap-3 rounded-md border border-copper/40 bg-copper/15 px-4 py-2.5" data-testid={`reservation-pending-${r.id}`}>
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-copper">
            À valider{r.reminder_count ? ` — ${r.reminder_count} relance(s) envoyée(s)` : " — alerte envoyée"}
          </span>
          <div className="ml-auto flex gap-2">
            <button onClick={() => setStatus(r.id, "confirmee")} className="flex h-8 items-center gap-1.5 rounded-md bg-gold px-3 text-[11px] font-semibold uppercase tracking-wider text-ink transition-opacity hover:opacity-85" data-testid={`reservation-accept-${r.id}`}>
              <Check className="h-3.5 w-3.5" strokeWidth={2} />
              Accepter
            </button>
            <button onClick={() => setStatus(r.id, "refusee")} className="flex h-8 items-center gap-1.5 rounded-md border border-red-400/50 px-3 text-[11px] font-semibold uppercase tracking-wider text-red-400 transition-colors hover:bg-red-400/10" data-testid={`reservation-refuse-${r.id}`}>
              <Ban className="h-3.5 w-3.5" strokeWidth={1.5} />
              Refuser
            </button>
            {r.phone && (
              <a href={`tel:${r.phone.replace(/\s/g, "")}`} className="flex h-8 items-center gap-1.5 rounded-md border border-white/15 px-3 text-[11px] font-semibold uppercase tracking-wider text-ivory/70 transition-colors hover:border-gold hover:text-gold" data-testid={`reservation-call-${r.id}`}>
                <Phone className="h-3.5 w-3.5" strokeWidth={1.5} />
                Appeler
              </a>
            )}
          </div>
        </div>
      )}
      <div className="min-w-40">
        <p className="text-sm font-semibold text-ivory">{r.name}</p>
        <p className="text-xs text-ivory/45">{r.phone} · {r.email}</p>
      </div>
      <div className="text-sm text-ivory/70">
        {r.date} · {r.time} · {r.guests} pers.
        {r.table_name ? <span className="ml-2 text-xs text-copper">· Table {r.table_name}</span> : null}
        {r.deposit_amount ? (
          <span className="ml-2 text-xs text-gold">· Acompte {String(r.deposit_amount).replace(".", ",")} €</span>
        ) : null}
        {r.reminder_sent ? <span className="ml-2 text-xs text-ivory/35">· rappel envoyé</span> : null}
      </div>
      {r.message && <p className="w-full text-xs text-ivory/45">{r.message}</p>}
      {r.pre_order?.length > 0 && (
        <p className="w-full text-xs text-gold/70">
          Pré-commande : {r.pre_order.map((p) => `${p.quantity}× ${p.name}`).join(", ")} — estimé {String(r.estimated_total).replace(".", ",")} €
        </p>
      )}
      <div className="ml-auto flex items-center gap-3">
        <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
          <SelectTrigger className="!h-9 w-36 border-white/10 bg-white/5 text-xs text-ivory" data-testid={`reservation-status-${r.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-coal text-ivory">
            {Object.entries(STATUS_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button onClick={() => setEditing({ ...emptyReservation, ...r })} aria-label="Modifier" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`reservation-edit-${r.id}`}>
          <Pencil className="h-4 w-4" strokeWidth={1.5} />
        </button>
        {waReminderLink(r) && (
          <a href={waReminderLink(r)} target="_blank" rel="noopener noreferrer" aria-label="Envoyer un rappel WhatsApp" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-[#25D366] hover:text-[#25D366]" data-testid={`reservation-whatsapp-${r.id}`}>
            <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
          </a>
        )}
        <button onClick={() => remove(r.id)} aria-label="Supprimer" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`reservation-delete-${r.id}`}>
          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="reservations-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ivory">Réservations — {items.length}</h2>
        <div className="flex gap-3">
          <button onClick={runReminders} className="btn-outline !h-10 !px-5 !text-[11px]" data-testid="reminders-run-button">
            Envoyer les rappels de demain
          </button>
          <button onClick={() => setEditing({ ...emptyReservation })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="reservation-add-button">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Ajouter (téléphone, WhatsApp, sur place)
          </button>
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2" data-testid="reservations-subtabs">
        {[
          { id: "liste", label: "Liste", icon: List },
          { id: "calendrier", label: "Calendrier", icon: CalendarDays },
          { id: "tables", label: `Tables (${tables.length})`, icon: Users },
          { id: "creneaux", label: `Créneaux bloqués (${blocked.length})`, icon: Ban },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            data-testid={`reservations-subtab-${t.id}`}
            className={`flex items-center gap-2 rounded-md border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
              sub === t.id ? "border-copper bg-copper text-ivory" : "border-white/10 text-ivory/60 hover:border-copper/50 hover:text-ivory"
            }`}
          >
            <t.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t.label}
          </button>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="reservation-edit-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier la réservation" : "Nouvelle réservation (sans paiement)"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input required value={editing.name} onChange={set("name")} placeholder="Nom et prénom" className="input-dark !h-11" data-testid="res-name-input" />
              <input value={editing.phone} onChange={set("phone")} placeholder="Téléphone" className="input-dark !h-11" data-testid="res-phone-input" />
            </div>
            <input type="email" value={editing.email} onChange={set("email")} placeholder="Email" className="input-dark !h-11" data-testid="res-email-input" />
            <div className="grid gap-4 sm:grid-cols-3">
              <input required type="date" value={editing.date} onChange={set("date")} className="input-dark !h-11 [color-scheme:dark]" data-testid="res-date-input" />
              <input required value={editing.time} onChange={set("time")} placeholder="20h00" className="input-dark !h-11" data-testid="res-time-input" />
              <input type="number" min="1" value={editing.guests} onChange={set("guests")} className="input-dark !h-11" data-testid="res-guests-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Table (optionnel)</label>
              <Select value={editing.table_name || "aucune"} onValueChange={(v) => setEditing((s) => ({ ...s, table_name: v === "aucune" ? "" : v }))}>
                <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="res-table-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  <SelectItem value="aucune">Aucune table assignée</SelectItem>
                  {tables.map((t) => (
                    <SelectItem key={t.id} value={t.name}>{t.name} — {t.seats} places</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <textarea rows={2} value={editing.message} onChange={set("message")} placeholder="Message / précisions" className="input-dark !h-auto py-3" data-testid="res-message-input" />
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Statut</label>
              <Select value={editing.status} onValueChange={(v) => setEditing((s) => ({ ...s, status: v }))}>
                <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="res-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-coal text-ivory">
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="res-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      {sub === "liste" && (
        items.length === 0 ? (
          <p className="text-sm text-ivory/40" data-testid="reservations-empty">Aucune réservation pour le moment.</p>
        ) : (
          <div className="space-y-4" data-testid="reservations-list">{items.map(renderReservation)}</div>
        )
      )}

      {sub === "calendrier" && (
        <div data-testid="reservations-calendar">
          <div className="mb-6 flex items-center gap-4">
            <button onClick={() => shiftMonth(-1)} aria-label="Mois précédent" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 hover:border-gold hover:text-gold" data-testid="calendar-prev">
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <h3 className="min-w-44 text-center text-base font-semibold text-ivory" data-testid="calendar-month-label">
              {MONTH_NAMES[calMonth.month]} {calMonth.year}
            </h3>
            <button onClick={() => shiftMonth(1)} aria-label="Mois suivant" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-ivory/60 hover:border-gold hover:text-gold" data-testid="calendar-next">
              <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-ivory/35">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
              <div key={d} className="py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1.5" data-testid="calendar-grid">
            {calendarCells.map((cell, i) =>
              cell === null ? (
                <div key={`empty-${i}`} />
              ) : (
                <button
                  key={cell.iso}
                  onClick={() => setCalDay(calDay === cell.iso ? null : cell.iso)}
                  data-testid={`calendar-day-${cell.iso}`}
                  className={`min-h-16 rounded-md border p-2 text-left transition-colors ${
                    calDay === cell.iso
                      ? "border-copper bg-copper/15"
                      : cell.blocked
                        ? "border-red-400/30 bg-red-400/5"
                        : cell.reservations.length > 0
                          ? "border-gold/30 bg-gold/5 hover:border-gold/60"
                          : "border-white/5 hover:border-white/20"
                  }`}
                >
                  <span className="text-xs text-ivory/70">{cell.day}</span>
                  {cell.reservations.length > 0 && (
                    <p className="mt-1 text-[10px] font-semibold text-gold">
                      {cell.reservations.length} résa · {cell.reservations.reduce((s, r) => s + (r.guests || 0), 0)} pers.
                    </p>
                  )}
                  {cell.blocked && <p className="mt-1 text-[10px] font-semibold text-red-400/80">Bloqué</p>}
                </button>
              )
            )}
          </div>
          {calDay && (
            <div className="mt-6 space-y-4" data-testid="calendar-day-detail">
              <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gold">
                Réservations du {calDay}
              </h4>
              {(byDay[calDay] || []).length === 0 ? (
                <p className="text-sm text-ivory/40">Aucune réservation ce jour.</p>
              ) : (
                (byDay[calDay] || []).map(renderReservation)
              )}
            </div>
          )}
        </div>
      )}

      {sub === "tables" && (
        <div className="max-w-2xl" data-testid="tables-manager">
          <form onSubmit={addTable} className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-white/10 bg-coal/60 p-5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Nom de la table</label>
              <input value={newTable.name} onChange={(e) => setNewTable((t) => ({ ...t, name: e.target.value }))} placeholder="Ex : T1, Terrasse 2…" className="input-dark !h-11 w-44" data-testid="table-name-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Places</label>
              <input type="number" min="1" value={newTable.seats} onChange={(e) => setNewTable((t) => ({ ...t, seats: e.target.value }))} className="input-dark !h-11 w-24" data-testid="table-seats-input" />
            </div>
            <button type="submit" className="btn-primary !h-11 !px-5 !text-[11px]" data-testid="table-add-button">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Ajouter
            </button>
          </form>
          {tables.length === 0 ? (
            <p className="text-sm text-ivory/40" data-testid="tables-empty">
              Aucune table. Créez vos tables pour les assigner aux réservations.
            </p>
          ) : (
            <div className="space-y-2" data-testid="tables-list">
              {tables.map((t) => (
                <div key={t.id} className="flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`table-row-${t.id}`}>
                  <span className="text-sm font-semibold text-ivory">{t.name}</span>
                  <span className="text-xs text-ivory/50">{t.seats} places</span>
                  <button onClick={() => removeTable(t.id)} aria-label="Supprimer" className="ml-auto flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`table-delete-${t.id}`}>
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sub === "creneaux" && (
        <div className="max-w-2xl" data-testid="blocked-slots-manager">
          <form onSubmit={addSlot} className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-white/10 bg-coal/60 p-5">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Date</label>
              <input type="date" value={newSlot.date} onChange={(e) => setNewSlot((s) => ({ ...s, date: e.target.value }))} className="input-dark !h-11 [color-scheme:dark]" data-testid="slot-date-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Horaire (vide = journée)</label>
              <input value={newSlot.time} onChange={(e) => setNewSlot((s) => ({ ...s, time: e.target.value }))} placeholder="Ex : 20:00" className="input-dark !h-11 w-32" data-testid="slot-time-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Motif</label>
              <input value={newSlot.reason} onChange={(e) => setNewSlot((s) => ({ ...s, reason: e.target.value }))} placeholder="Ex : privatisation" className="input-dark !h-11 w-44" data-testid="slot-reason-input" />
            </div>
            <button type="submit" className="btn-primary !h-11 !px-5 !text-[11px]" data-testid="slot-add-button">
              <Ban className="h-4 w-4" strokeWidth={1.5} />
              Bloquer
            </button>
          </form>
          {blocked.length === 0 ? (
            <p className="text-sm text-ivory/40" data-testid="blocked-slots-empty">
              Aucun créneau bloqué. Les créneaux bloqués refusent automatiquement les nouvelles réservations.
            </p>
          ) : (
            <div className="space-y-2" data-testid="blocked-slots-list">
              {blocked.map((s) => (
                <div key={s.id} className="flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`blocked-slot-${s.id}`}>
                  <span className="text-sm text-ivory">{s.date}</span>
                  <span className="text-xs text-ivory/50">{s.time || "Toute la journée"}</span>
                  {s.reason && <span className="text-xs text-ivory/40">· {s.reason}</span>}
                  <button onClick={() => removeSlot(s.id)} aria-label="Débloquer" className="ml-auto flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`blocked-slot-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
